import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  DateRange,
  extractDateRange,
  extractDefaultStoreIds,
  extractNumber,
  HxReportService,
  HxUiLabelPipe,
  isoDate,
  PaymentApprovalFromReportModel,
  SupplierPaymentModel,
  TimeInterval,
  toRouterQueryParams
} from 'hx-services';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { eachDayOfInterval, eachMonthOfInterval, eachWeekOfInterval, eachYearOfInterval, parseISO, subDays } from 'date-fns';
import { Subscription } from 'rxjs';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { NgbModal, NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { PaymentApprovalNewFromReportComponent } from '@manager-app/storage/payment-approval/new-from-report/payment-approval-new-from-report.component';
import { HxCheckboxComponent, HxDateRangeSelectComponent, HxEnumSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { SuppliersSearchComponent } from '../../components/suppliers-search/suppliers-search.component';
import { DatePipe, DecimalPipe, NgFor, NgIf } from '@angular/common';
import { ContextMenuModule } from '@perfectmemory/ngx-contextmenu';
import { TranslocoPipe } from '@jsverse/transloco';

interface SupplierPaymentInfoModel extends SupplierPaymentModel {
  storeIds?: number[];
  dateFrom?: string;
  dateTo?: string;
}

interface DateInterval {
  date: string;
  startDate: string;
  endDate: string;
}

interface CommonParams {
  page?: number,
  limit?: number,
  storeIds?: number[],
  date?: DateRange,
  supplierId?: number,
  filterByCreateDate?: boolean,
  paid?: boolean,
  timeInterval?: TimeInterval,
}

@Component({
    selector: 'app-supplier-payment.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './supplier-payment.component.html',
    styleUrls: ['./supplier-payment.component.css'],
    imports: [HxStoreSelectComponent, FormsModule, HxDateRangeSelectComponent, HxEnumSelectComponent, SuppliersSearchComponent, HxCheckboxComponent, NgIf, NgFor, ContextMenuModule, RouterLink, HxLoaderComponent, NgbPagination, DecimalPipe, DatePipe, TranslocoPipe, HxUiLabelPipe]
})
export class SupplierPaymentComponent implements OnInit, OnDestroy {
  list: SupplierPaymentInfoModel[] = [];
  TimeInterval = TimeInterval;
  params: CommonParams = {page: 1, limit: 20, timeInterval: TimeInterval.day};
  pagination = {
    allItemCount: 0
  };
  isLoading = {
    list: false
  };
  formatData: string[] = [];
  dates: DateInterval[] = [];
  years: number[] = [];
  totalBySupplierMap = new Map<string, number>();
  totalByDateMap = new Map<string, number>();
  totalByDateAndSupplierMap = new Map<string, number>();
  waitPaymentTotalByDateMap = new Map<string, number>();
  total: number;
  private sub: Subscription;

  constructor(
    private reportService: HxReportService,
    private errorService: ErrorHandlerService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private modal: NgbModal,
  ) {
  }

  ngOnInit() {
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      const asBooleanFn = (name: string) => paramMap.get(name) ? paramMap.get(name) === 'true' : undefined;
      const asArrFn = (name: string, mapFn: (key: string) => any) => paramMap.get(name)?.split(',').map(mapFn) ?? undefined;
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(new Date()), to: isoDate(new Date())};
      this.params.storeIds = asArrFn('storeIds', str => Number(str)) ?? extractDefaultStoreIds();
      this.params.supplierId = extractNumber('supplierId', paramMap);
      this.params.paid = asBooleanFn('paid');
      this.params.timeInterval = paramMap.get('timeInterval') as TimeInterval ?? TimeInterval.day;
      this.params.filterByCreateDate = asBooleanFn('filterByCreateDate');
      this.getDaysArray(this.params.date.from, this.params.date.to);
      this.loadList();
      if (this.params.date) {
        const dateInRange = eachDayOfInterval({start: parseISO(this.params.date.from), end: parseISO(this.params.date.to)});
        this.formatData = dateInRange.map(e => isoDate(e));
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  applyFilters() {
    this.navigate();
  }

  resetFilter() {
    this.params = {page: 1, limit: 20, storeIds: this.params.storeIds, timeInterval: TimeInterval.day};
    this.navigate();
  }

  pageChanged(event: number) {
    this.params.page = event;
    this.navigate();
  }

  getNumberOfDates(year: number): number {
    return this.dates.filter(d => new Date(d.startDate).getFullYear() === year).length;
  }

  createPaymentApproval(params: {payment: SupplierPaymentInfoModel, date?: string}) {
    const item = params.payment;
    const date = params.date;
    const modalRef = this.modal.open(PaymentApprovalNewFromReportComponent, {size: 'lg'});
    let data: PaymentApprovalFromReportModel = {} as PaymentApprovalFromReportModel;
    data.supplierId = item.supplierId;
    data.supplierName = item.supplierName;
    data.storeId = item.storeId;
    data.storeTitle = item.storeName.ru;

    const dateFrom = date ?? item.dateFrom;
    const dateTo = date ?? item.dateTo;

    if (this.params.filterByCreateDate) {
      data.createDateFrom = dateFrom;
      data.createDateTo = dateTo;
    } else {
      data.dueDateFrom = dateFrom;
      data.dueDateTo = dateTo;
    }
    modalRef.componentInstance.data = data;
    modalRef.result.then(() => {
      this.loadList();
      modalRef.dismiss();
    }, () => {
      modalRef.dismiss();
    });
  }

  showCredit(params: {payment: SupplierPaymentInfoModel, param: CommonParams}) {
    let createDateFrom: string, createDateTo: string, dueDateFrom: string, dueDateTo: string;
    if (params.param.filterByCreateDate) {
      createDateFrom = params.param.date.from;
      createDateTo = params.param.date.to;
      dueDateFrom = undefined;
      dueDateTo = undefined;
    } else {
      dueDateFrom = params.param.date.from;
      dueDateTo = params.param.date.to;
      createDateFrom = undefined;
      createDateTo = undefined;
    }
    this.router.navigate(['/storage/invoices'], {
      queryParams: {
        createDateFrom: createDateFrom,
        createDateTo: createDateTo,
        dueDateFrom: dueDateFrom,
        dueDateTo: dueDateTo,
        supplierId: params.payment.supplierId,
        storeIds: [params.payment.storeId],
        paid: params.param.paid
      }
    });
  }

  showDebit(params: {payment: SupplierPaymentInfoModel, param: CommonParams}) {
    this.router.navigate(['/storage/payment-approvals'], {
      queryParams: {
        dateFrom: params.param.date.from,
        dateTo: params.param.date.to,
        supplierId: params.payment.supplierId,
        storeId: params.payment.storeId,
        paid: params.param.paid
      }
    });
  }

  private navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private loadList() {
    if (this.params.storeIds?.length) {
      this.list = [];
      this.total = 0;
      this.isLoading.list = true;
      this.totalByDateMap.clear();
      this.totalBySupplierMap.clear();
      this.totalByDateAndSupplierMap.clear();
      this.waitPaymentTotalByDateMap.clear();
      this.reportService.supplierPaymentReport(this.params).subscribe(response => {
        this.list = response.list;
        this.list.forEach(sp => {
          const sumTotal = sp.payments.reduce((sum, current) => sum + current.total, 0);
          this.totalBySupplierMap.set(sp.supplierId + '-' + sp.storeId, sumTotal);
          sp.payments.forEach(r => {
            if (this.totalByDateMap.has(r.date)) {
              const totalByDate = this.totalByDateMap.get(r.date);
              this.totalByDateMap.set(r.date, r.total + totalByDate);
            } else {
              this.totalByDateMap.set(r.date, r.total);
            }
            if (this.waitPaymentTotalByDateMap.has(sp.supplierId + '-' + r.date)) {
              const totalByDate = this.waitPaymentTotalByDateMap.get(sp.supplierId + '-' + r.date);
              this.waitPaymentTotalByDateMap.set(sp.supplierId + '-' + r.date, r.waitPaymentTotal + totalByDate);
            } else {
              this.waitPaymentTotalByDateMap.set(sp.supplierId + '-' + r.date, r.waitPaymentTotal);
            }
          });
          this.dates.forEach(d => {
            const total = sp.payments.filter(r => r.date === d.date).reduce((sum, current) => sum + current.total, 0);
            this.totalByDateAndSupplierMap.set(sp.supplierId + '-' + d.date + '-' + sp.storeId, total);
          });
          sp.storeIds = [sp.storeId];
        });
        this.totalByDateAndSupplierMap.forEach(v => this.total += v);
        this.pagination.allItemCount = response.count;
        this.isLoading.list = false;
      }, error => {
        this.isLoading.list = false;
        this.errorService.handler(error);
      });
    }
  }

  private getDaysArray(startDate: string, endDate: string) {
    this.dates = [];
    this.years = [];
    let days;
    if (this.params.timeInterval === TimeInterval.day) {
      days = eachDayOfInterval({start: new Date(startDate), end: new Date(endDate)});
    } else if (this.params.timeInterval === TimeInterval.month) {
      days = eachMonthOfInterval({start: new Date(startDate), end: new Date(endDate)});
    } else if (this.params.timeInterval === TimeInterval.week) {
      days = eachWeekOfInterval({start: new Date(startDate), end: new Date(endDate)}, { weekStartsOn: 1 });
    } else if (this.params.timeInterval === TimeInterval.year) {
      days = eachYearOfInterval({start: new Date(startDate), end: new Date(endDate)});
    }
    this.fillDates(days);
    this.years = eachYearOfInterval({
      start: new Date(startDate), end: new Date(endDate)
    }).map(r => r.getFullYear());
  }

  private fillDates(days: Date[]) {
    for (let i = 0; i < days.length; i++) {
      let di: DateInterval = {} as DateInterval;
      di.date = isoDate(days[i]);
      if (i === 0) {
        if (this.params.timeInterval === TimeInterval.day) {
          di.startDate = this.params.date.from;
          di.endDate = this.params.date.from;
        } else if (this.params.timeInterval === TimeInterval.year) {
          di.startDate = this.params.date.from;
          di.endDate = this.params.date.to;
        } else {
          di.startDate = this.params.date.from;
          di.endDate = isoDate(subDays(days[i + 1], 1));
        }
      } else {
        if (days.length > (i + 1)) {
          if (this.params.timeInterval === TimeInterval.day) {
            di.startDate =isoDate(days[i]);
            di.endDate =isoDate(days[i]);
          } else {
            di.startDate = isoDate(days[i]);
            di.endDate = isoDate(subDays(days[i + 1], 1));
          }
        } else {
          if (this.params.timeInterval === TimeInterval.day) {
            di.startDate = isoDate(days[i]);
            di.endDate = isoDate(days[i]);
          } else {
            di.startDate = isoDate(days[i]);
            di.endDate = this.params.date.to;
          }
        }
      }
      this.dates.push(di);
    }
  }
}
